/**
 * US-180 / F-099 — what each audit scope actually exposes, in a reviewer's words.
 *
 * Granting `residents` hands an external auditor the clinical record; before
 * this, the approver authorising it was shown the word "Residents". This is the
 * disclosure. It improves INFORMED CONSENT and deliberately does not improve
 * least privilege: an approver still cannot withhold part of a scope, so the
 * copy must never imply they can.
 *
 * The prose is hand-written — a generated sentence would be unreadable — but it
 * is PINNED to the derivation. `routesWrittenAgainst` is the exact set of routes
 * that scope unlocked on its own when the copy was written, taken from
 * ./auditScopeMap.generated. A test recomputes it; if a route's declared scopes
 * change, the lists diverge and the test names the route that appeared or
 * vanished, forcing the copy to be re-read. That is what stops this from
 * becoming another label that lies — the defect US-159 and US-175 removed twice.
 *
 * Note `employees`: it unlocks NO route by itself. It widens what other screens
 * show, which is why its `routesWrittenAgainst` is empty and its summary says so
 * outright. A disclosure derived only from the declarative `auditorScopes`
 * wrapper would have reported that it grants nothing at all.
 */
import type { AuditAccessScope } from "./enums";
import {
  AUDIT_SCOPE_ROUTES,
  AUDIT_SCOPE_MODIFIERS,
} from "./auditScopeMap.generated";

/** Reviewer-facing scope names. The ONE definition — surfaces must not re-declare. */
export const AUDIT_SCOPE_LABELS: Record<AuditAccessScope, string> = {
  residents: "Residents",
  incidents: "Incidents",
  forms: "Forms & submissions",
  employees: "Employees",
  gate_metrics: "Gate metrics",
};

export interface AuditScopeDisclosure {
  /** One line: what granting this scope means. */
  readonly summary: string;
  /** Concrete contents, in the reviewer's language — never route paths. */
  readonly includes: readonly string[];
  /** Routes this scope unlocked ALONE when the copy was written. Drift anchor. */
  readonly routesWrittenAgainst: readonly string[];
}

export const AUDIT_SCOPE_DISCLOSURE: Record<
  AuditAccessScope,
  AuditScopeDisclosure
> = {
  residents: {
    summary: "The resident's clinical record.",
    includes: [
      "Vital signs, with 90-day trend charts",
      "Care plans and care notes",
      "ADL records and activity documentation",
      "Appointments and external providers",
      "Emergency contacts",
      "Pharmacy and insurance details",
      "DNR status, shown beside the resident's name",
      "Bulk PDF export of the record",
    ],
    routesWrittenAgainst: [
      "facilities/[id]/adl-compliance",
      "facilities/[id]/appointments-tracking",
      "facilities/[id]/care-notes-analysis",
      "facilities/[id]/care-plans/[planId]",
      "facilities/[id]/emergency-contacts-audit",
      "facilities/[id]/external-provider-assignments/missing",
      "facilities/[id]/pdf-export-jobs",
      "facilities/[id]/pharmacy-insurance-completeness",
      "facilities/[id]/physician-completeness",
      "facilities/[id]/residents",
      "facilities/[id]/residents/[residentId]",
      "facilities/[id]/vital-signs-compliance",
      "group-homes/[id]/pdf-export-jobs",
      "residents/[residentId]/audit-summary",
      "sessions/[id]/pdf-export-jobs",
    ],
  },
  incidents: {
    summary: "Incident reports for the facility.",
    includes: [
      "Incident reports and their details",
      "Files attached to an incident",
    ],
    routesWrittenAgainst: [
      "facilities/[id]/incidents",
      "facilities/[id]/incidents/[incidentId]",
      "facilities/[id]/incidents/[incidentId]/attachments/[attachmentId]",
    ],
  },
  forms: {
    summary: "Submitted forms and their contents.",
    includes: [
      "Form submissions for the facility",
      "Per-resident form submissions",
    ],
    routesWrittenAgainst: [
      "facilities/[id]/residents/[residentId]/forms",
    ],
  },
  employees: {
    summary: "Staff identities attached to other records. Grants no screen on its own.",
    includes: [
      "The employee who submitted each form",
      "Which staff documented each care note",
      "An employees section in exports",
    ],
    routesWrittenAgainst: [
    ],
  },
  gate_metrics: {
    summary: "Appointment-gate compliance metrics.",
    includes: [
      "Gate metrics for the facility",
      "Force-completed gate records",
      "The gate-metrics export",
    ],
    routesWrittenAgainst: [
      "facilities/[id]/gate-metrics",
      "facilities/[id]/gate-metrics/force-completes",
    ],
  },
};

/**
 * Routes a grant holding exactly `granted` can reach.
 *
 * Scope checks are AND-semantics, so a route qualifies only when its WHOLE
 * declared set is a subset of the grant. `compliance-dashboard` requires
 * residents AND incidents, so a residents-only grant must not be told it can
 * reach it — that is the one place a naive implementation overstates.
 */
export function routesUnlockedBy(
  granted: readonly AuditAccessScope[],
): readonly string[] {
  const held = new Set<string>(granted);
  return AUDIT_SCOPE_ROUTES.filter((r) => r.scopes.every((s) => held.has(s))).map(
    (r) => r.route,
  );
}

/** What a scope changes about OTHER screens without unlocking one itself. */
export function modifiersFor(
  scope: AuditAccessScope,
): readonly { readonly route: string; readonly effect: string }[] {
  return AUDIT_SCOPE_MODIFIERS.filter((m) => m.scope === scope);
}
